// mui components
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// mui icons
import { Close } from '@mui/icons-material';
// import data
import { getSectionFields } from '../data/sectionFields';

// react hooks
import { useState, useEffect } from 'react';
import { useDispatch } from '@/store';
import { actions } from '@/slices/modals';

import { Device } from 'src/__generated__/graphql';

// styles
import { tabStyle } from 'src/features/createDeviceConfiguration/styles/styles';
import { DeviceProfileObservations } from '@features/deviceProfile';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { get } from 'lodash';

import { formatSourceColor } from 'src/utils/devices';
import { ENABLED_SENSOR_TYPES } from '@features/createDeviceConfiguration/data/initialData';
import dayjs from 'dayjs';


interface SummarySheetProps {
  device?: Device;
  onClose: () => void;
  open: boolean;
}

const attributes = {
  measurementRange: "Rango de medición",
  cableLength: "Largo de cable (m)",
  depth: "Profundidad (cm)",
  fmx21Code: "Código FMX21",
  pulseValue: "Valor de pulso",
  pulseWidth: "Ancho del pulso"
};

const SummarySheetConfiguration = ({ device, onClose, open }: SummarySheetProps) => {
  // This dialog has two views, one for desktop and one for mobile
  const [selected, setSelected] = useState('general');
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = tabStyle;
  const typeColor = formatSourceColor(device?.profile);
  const { _ } = useLingui();
  const dispatch = useDispatch();

  /**
   * Handles the change of the selected tab value.
   * @param {React.SyntheticEvent} event - The event that triggered the change.
   * @param {string} newValue - The new value of the selected tab.
   */
  const handleTabValue = (event: React.SyntheticEvent, newValue: string) => {
    setSelected(newValue);
  };

  useEffect(() => {
    setSelected('general');
  }, [device?.id]);

  const handleConfigUpdate = () => {
    if (!device?.id) return;
    dispatch(actions.setTriggerConfigUpdateModal({ open: true, deviceId: device.id }));
  }

  /**
   * Formats the input based on the given name.
   * @param name - The name of the input.
   * @param input - The input value to be formatted.
   * @returns The formatted input value.
   */
  const formatInput = (name: string, input: any) => {
    switch (name) {
      case 'hardwareConfiguration.profile.dateAction':
        return new Date(input).toLocaleDateString();
      case 'hardwareConfiguration.approved':
        return input ? 'Sí' : 'No';
      case 'hardwareConfiguration.usbExtraction':
        return input ? 'Sí' : 'No';
      case 'hardwareConfiguration.googleDriveUrl':
        return (
          <Link href={input} target="_blank" rel="noopener noreferrer" style={{ color: '#FF9E42' }}>
            VER
          </Link>
        );
      case 'hardwareConfiguration.configurationFile.name':
        return (
          <Box display="flex" flexDirection="column">
            <Box display="flex" flexDirection="row" alignItems="center">
              <Link
                href={device?.hardwareConfiguration?.configurationFile?.url ?? ''}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#FF9E42' }}
              >
                <Typography>
                  {device?.hardwareConfiguration?.configurationFile?.name}
                </Typography>
              </Link>
              <Button
                sx={{
                  ml: 2
                }}
                variant="outlined"
                onClick={handleConfigUpdate}
              >
                Actualizar
              </Button>
            </Box>
            <Typography>
              {device?.hardwareConfiguration?.configurationFile?.lastModified ? `Fecha de última actualización: ${dayjs.unix(device?.hardwareConfiguration?.configurationFile?.lastModified).format('DD/MM/YYYY')}` : ''}
            </Typography>
          </Box>
        )
      default:
        return input;
    }
  };
  /**
   * Formats the input value for display in the summary sheet.
   * If the input is null, undefined or an empty string, it will display a dash ("-").
   * Otherwise, it will call the formatInput function to format the input.
   *
   * @param name - The name of the input field.
   * @param input - The input value to be formatted.
   * @returns The formatted input value or a dash ("-") if the input is null, undefined or an empty string.
   */
  const filterInput = (name: string, input: any) => {
    return input ? formatInput(name, input) : '-';
  };

  /**
   * Checks if the input is displayed or not.
   * @param input - The input to check.
   * @returns A boolean indicating whether the input is displayed or not.
   */
  const isDisplayed = (input: any) => {
    return input.displayed === undefined || input.displayed(device) === true;
  };

  /**
   * Closes the Summary Sheet Configuration dialog and resets the selected tab to "general".
   * @returns void
   */
  const handleClose = () => {
    setSelected('general');
    onClose();
  };

  const isIntegration = (device: Device): Boolean => {
    return device?.profile?.source === 'integration';
  };

  const renderMobileTab = (value: string, label: React.ReactNode) => (
    <Grid key={value} item xs={6}>
      <Button
        fullWidth
        onClick={() => setSelected(value)}
        sx={{
          backgroundColor: selected === value ? '#FFFFFF !important' : '#212A35',
          borderRadius: '10px',
          border: '2px solid #FFFFFF',
        }}
      >
        <Typography
          sx={{
            color: selected === value ? '#212A35' : '#FFFFFF',
            fontWeight: 'semibold',
            fontSize: '16px',
            fontFamily: 'Sora',
            fontColor: '#FFFFFF',
          }}
        >
          {label}
        </Typography>
      </Button>
    </Grid>
  );

  const sectionFields = getSectionFields();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth fullScreen={mobileDevice} maxWidth="xl">
      {mobileDevice ? (
        <>
          <DialogTitle>
            <Box
              sx={{
                color: '#FF9E42',
                fontWeight: 'bold',
                fontFamily: 'Sora',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {device?.profile.name}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 'bold',
                }}
              >
                ID {device?.id}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: '#FFFFFF',
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <Grid container spacing={2} p={1} px={2}>
            {Object.keys(sectionFields)
              .filter(
                (section) => device?.profile?.source !== 'integration' || section === 'general',
              )
              .map((section) => renderMobileTab(section, sectionFields[section].label))}
            {renderMobileTab('observations', <Trans>Observaciones</Trans>)}
          </Grid>
        </>
      ) : (
        <>
          <DialogTitle
            sx={{
              backgroundColor: '#2a3542',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="h5">{device?.profile.name}</Typography>
              <Typography>-</Typography>
              <Typography>
                ID <b>{device?.id}</b>
              </Typography>
              <Typography>-</Typography>
              <Typography color={typeColor}>
                <b>{device?.profile?.type}</b>
              </Typography>
            </Box>
          </DialogTitle>

          <Grid
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Tabs
              value={selected}
              onChange={handleTabValue}
              variant="fullWidth"
              centered
              sx={{
                width: '100%',
              }}
              TabIndicatorProps={{
                sx: {
                  display: 'none',
                },
              }}
            >
              <Tab
                value="general"
                key="general"
                sx={{
                  ...(isIntegration(device)
                    ? classes.singleTab
                    : selected === 'general'
                      ? classes.selectedLeftTab
                      : classes.leftTab),
                }}
                label={_(msg`General`)}
              />
              {!isIntegration(device) && (
                <Tab
                  value="sensorization"
                  key="sensorization"
                  sx={{
                    ...(selected === 'sensorization' ? classes.selectedTab : classes.tab),
                  }}
                  label={_(msg`Sensorización`)}
                />
              )}
              {!isIntegration(device) && (
                <Tab
                  value="energization"
                  key="energization"
                  sx={{
                    ...(selected === 'energization' ? classes.selectedTab : classes.tab),
                  }}
                  label={_(msg`Energización`)}
                />
              )}
              {!isIntegration(device) && (
                <Tab
                  value="processor"
                  key="processor"
                  sx={{
                    ...(selected === 'processor' ? classes.selectedTab : classes.tab),
                  }}
                  label={_(msg`Data logger`)}
                />
              )}
              {!isIntegration(device) && (
                <Tab
                  value="connectivity"
                  key="connectivity"
                  sx={{
                    ...(selected === 'connectivity' ? classes.selectedTab : classes.tab),
                  }}
                  label={_(msg`Conectividad`)}
                />
              )}
              <Tab
                value="observations"
                key="observations"
                sx={{
                  ...(selected === 'observations' ? classes.selectedRightTab : classes.rightTab),
                }}
                label={_(msg`Observaciones`)}
              />
            </Tabs>
          </Grid>
        </>
      )}

      <DialogContent
        sx={{
          height: '60vh',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            height: '100%',
          }}
        >
          {selected === 'general' && device?.hardwareConfiguration?.images ? (
            <Box
              component="img"
              alt={_(msg`Imagen del dispositivo`)}
              src={device.hardwareConfiguration.images}
              sx={{
                height: { xs: 'auto' },
                width: { xs: '100%', md: 400 },
                paddingY: 2,
                marginLeft: 'auto',
                marginRight: 'auto',
                display: 'block',
              }}
            />
          ) : null}

          {selected === 'observations' && device?.id && (
            <DeviceProfileObservations deviceId={device.id} />
          )}
          {Object.keys(sectionFields)
            .filter((section) => device?.profile?.source !== 'integration' || section === 'general')
            .map((section) => (
              <Box
                key={section}
                sx={{
                  display: selected === section ? 'block' : 'none',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 'semibold',
                    fontSize: '20px',
                    fontFamily: 'Sora',
                    display: mobileDevice ? 'block' : 'none',
                  }}
                >
                  {sectionFields[section].label}
                </Typography>
                <TableContainer sx={{ width: '100%' }}>
                  <Table aria-label="simple table">
                    <TableBody>
                      {section === 'sensorization'
                        ? device?.sensors.filter(sensor => ENABLED_SENSOR_TYPES.includes(sensor.type.label ?? "")).map((sensor, index) => (
                          <>
                            <Typography color="textSecondary" mt={2}>
                              <Trans>Sensor</Trans> {index + 1} - {sensor?.type?.label}
                            </Typography>
                            <TableRow key={`${device?.id}-${section}-${index}-model`}>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                }}
                                align="left"
                              >
                                <Trans>Modelo</Trans>
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                  wordBreak: 'break-word',
                                }}
                                align="left"
                              >
                                {sensor.configuration?.sensorModelOption?.name || '-'}
                              </TableCell>
                            </TableRow>
                            {sensor.configuration?.sensorModelOption?.name === 'Integración' && (
                              <TableRow key={`${device?.id}-${section}-${index}-integrationDetail`}>
                                <TableCell
                                  sx={{
                                    padding: mobileDevice ? 0.5 : 2,
                                  }}
                                  align="left"
                                >
                                  <Trans>Detalle de integración</Trans>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    padding: mobileDevice ? 0.5 : 2,
                                    wordBreak: 'break-word',
                                  }}
                                  align="left"
                                >
                                  {sensor.configuration?.integrationDetail || '-'}
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow key={`${device?.id}-${section}-${index}-serialNumber`}>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                }}
                                align="left"
                              >
                                <Trans>Número de Serie</Trans>
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                  wordBreak: 'break-word',
                                }}
                                align="left"
                              >
                                {sensor.configuration?.serialNumber || '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow key={`${device?.id}-${section}-${index}-batchNumber`}>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                }}
                                align="left"
                              >
                                <Trans>Número de lote</Trans>
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                  wordBreak: 'break-word',
                                }}
                                align="left"
                              >
                                {sensor.configuration?.batchNumber || '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow key={`${device?.id}-${section}-${index}-modbusId`}>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                }}
                                align="left"
                              >
                                <Trans>Modbus ID</Trans>
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                  wordBreak: 'break-word',
                                }}
                                align="left"
                              >
                                {sensor.configuration?.modbusId || '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow key={`${device?.id}-${section}-${index}-factorM`}>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                }}
                                align="left"
                              >
                                <Trans>Factor M</Trans>
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                  wordBreak: 'break-word',
                                }}
                                align="left"
                              >
                                {sensor.configuration?.factorM || '-'}
                              </TableCell>
                            </TableRow>
                            <TableRow key={`${device?.id}-${section}-${index}-offsetN`}>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                }}
                                align="left"
                              >
                                <Trans>Offset N</Trans>
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: mobileDevice ? 0.5 : 2,
                                  wordBreak: 'break-word',
                                }}
                                align="left"
                              >
                                {sensor.configuration?.offsetN || '-'}
                              </TableCell>
                            </TableRow>
                            {(() => {
                              if (!sensor.configuration?.attributes) return null;
                              try {
                                const parsed = typeof sensor.configuration.attributes === 'string'
                                  ? JSON.parse(sensor.configuration.attributes)
                                  : sensor.configuration.attributes;
                                const formatted = Object.keys(parsed)
                                  .filter(key => key in attributes && parsed[key])
                                  .map(key => `${attributes[key as keyof typeof attributes]}: ${parsed[key]}`)
                                  .join(', ');
                                if (!formatted) return null;
                                return (
                                  <TableRow key={`${device?.id}-${section}-${index}-attributes`}>
                                    <TableCell
                                      sx={{
                                        padding: mobileDevice ? 0.5 : 2,
                                      }}
                                      align="left"
                                    >
                                      <Trans>Atributos adicionales</Trans>
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        padding: mobileDevice ? 0.5 : 2,
                                        wordBreak: 'break-word',
                                      }}
                                      align="left"
                                    >
                                      {formatted}
                                    </TableCell>
                                  </TableRow>
                                );
                              } catch {
                                return null;
                              }
                            })()}
                          </>
                        ))
                        : sectionFields[section].inputs.map(
                          (input) =>
                            device &&
                            isDisplayed(input) && (
                              <TableRow
                                sx={{ width: '100%', fontSize: '18px' }}
                                key={`${device?.id}-${section}-${input.name}`}
                              >
                                <TableCell
                                  sx={{
                                    padding: mobileDevice ? 0.5 : 2,
                                  }}
                                  align="left"
                                >
                                  {input.label}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    padding: mobileDevice ? 0.5 : 2,
                                    wordBreak: 'break-word',
                                  }}
                                  align="left"
                                >
                                  {filterInput(input.name, get(device, input.name, ''))}
                                </TableCell>
                              </TableRow>
                            ),
                        )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SummarySheetConfiguration;
